import { useState } from "preact/hooks"
import { Download, FileSpreadsheet, Loader2 } from "lucide-react"
import { Button } from "../../../../components/ui/button"
import { toast } from "@/components/ui/use-toast.ts"
import { download_post_export } from "@/utils/download"
import { classes } from "../../../../utils"

interface PromotionDownloadButtonProps {
  promotion_ids: string[]
  url: string
  fallback_filename: string
  label: string
  title: string
  icon: typeof Download
  className?: string
}

/** POSTs the promotions shown to a download endpoint and saves what comes back. */
function PromotionDownloadButton({ promotion_ids, url, fallback_filename, label, title, icon: Icon, className }: PromotionDownloadButtonProps) {
  const [loading, setLoading] = useState(false)

  async function handle_download() {
    if (loading || promotion_ids.length === 0) return
    setLoading(true)
    try {
      await download_post_export(url, { promotion_ids }, fallback_filename)
    } catch (error) {
      toast({
        title: `${label} failed`,
        description: error instanceof Error ? error.message : "Request failed",
        variant: "destructive",
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    <Button
      variant="outline"
      size="sm"
      className={className}
      disabled={loading || promotion_ids.length === 0}
      onClick={handle_download}
      title={title}
    >
      {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Icon className="h-4 w-4 mr-2" />}
      {label}
    </Button>
  )
}

interface PromotionDownloadsProps {
  /** The promotions currently shown; every deduction charged to them is gathered. */
  promotion_ids: string[]
  className?: string
}

/**
 * Downloads for the promotions shown: an Excel workbook of the promotions themselves, a spreadsheet
 * of each promotion with the deductions charged to it, and one ZIP of their backup (a folder per promotion number) — instead of opening each
 * deduction in turn.
 */
export function PromotionDownloads({ promotion_ids, className }: PromotionDownloadsProps) {
  return (
    <div class={classes("flex flex-wrap gap-2", className)}>
      <PromotionDownloadButton
        promotion_ids={promotion_ids}
        url="/api/promotions/export"
        fallback_filename="Promotions.xlsx"
        label="Export promotions"
        title="An Excel workbook of these promotions, one row each: number, customer, retailer, stage, dates, products, discount, planned, latest estimate and actual spend"
        icon={FileSpreadsheet}
      />
      <PromotionDownloadButton
        promotion_ids={promotion_ids}
        url="/api/promotions/deductions-export"
        fallback_filename="Promotion_deductions.csv"
        label="Export deductions"
        title="A spreadsheet of these promotions with every deduction charged to each: invoice, check, amount charged, stage, backup and a link"
        icon={FileSpreadsheet}
      />
      <PromotionDownloadButton
        promotion_ids={promotion_ids}
        url="/api/promotions/backup-zip"
        fallback_filename="Promotion_backup.zip"
        label="Download backup"
        title="One ZIP of the backup for every deduction charged to these promotions, a folder per promotion number"
        icon={Download}
      />
    </div>
  )
}
